import axios, { AxiosError, AxiosInstance, AxiosProgressEvent, AxiosResponse } from 'axios';
import { ERR } from '../lib/apiErrors';

// API response types based on documentation
export interface ApiSuccessResponse<T = any> {
  success: true;
  data: T;
  message: string;
}

export interface ApiErrorResponse {
  success: false;
  error: {
    /**
     * HTTP status code (number) or a backend machine code (string, e.g.
     * `BUYER_FORBIDDEN`). The shared `apiErrors` helpers branch on whichever
     * shape the backend emits; never assume one or the other at call sites —
     * use `isApiErrorCode()` from `lib/apiErrors`.
     */
    code: number | string;
    /**
     * App-specific machine code surfaced by the backend (e.g. `COUNTER_LIMIT`).
     * Distinct from `code` which is usually the HTTP status. Use when call
     * sites need to branch on a specific business rule rejection.
     */
    appCode?: string;
    message: string;
    details?: any;
  };
}

export type ApiResponse<T = any> = ApiSuccessResponse<T> | ApiErrorResponse;

// Base URLs from API documentation
const API_BASE_URL = {
  development: 'http://localhost:5001/v1',
  production: 'https://api.creators.ankaragrid.africa/v1'
};

// Use environment variable if available, otherwise fall back to environment-based selection
const BASE_URL = import.meta.env.VITE_API_URL || 
  (import.meta.env.PROD ? API_BASE_URL.production : API_BASE_URL.development);

console.log('🔧 API Client Configuration:', {
  VITE_API_URL: import.meta.env.VITE_API_URL,
  PROD: import.meta.env.PROD,
  BASE_URL,
  mode: import.meta.env.MODE
});

/**
 * Uploads are bounded by minutes, not seconds: a 10-photo archive post is
 * several MB, and on a slow mobile uplink that legitimately takes minutes.
 * The old 15s default aborted those uploads mid-flight and surfaced as a
 * generic "unexpected error".
 */
const UPLOAD_TIMEOUT_MS = 5 * 60 * 1000;

export interface UploadOptions {
  /** Fires as bytes leave the device — drives real progress UI. */
  onUploadProgress?: (event: AxiosProgressEvent) => void;
  /** Lets the caller abort an in-flight upload (user pressed Cancel). */
  signal?: AbortSignal;
  /** Override the upload deadline. Defaults to UPLOAD_TIMEOUT_MS. */
  timeout?: number;
}

class ApiClient {
  private instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: BASE_URL,
      // 15s is right for JSON calls, but it is a hard deadline on the whole
      // request — including the time spent writing the body. File uploads
      // therefore override it (see UPLOAD_TIMEOUT_MS / uploadFile).
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors = (): void => {
    // Request interceptor - Add auth token if available
    this.instance.interceptors.request.use(
      (config) => {
        
        const token = localStorage.getItem('accessToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor - Handle token refresh on 401
    this.instance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config;
        
        // CRITICAL: Don't try to refresh if the failing request IS the refresh endpoint
        // This prevents infinite loops
        if (originalRequest?.url?.includes('/auth/refresh')) {
          // Refresh endpoint itself failed, clear tokens and redirect
          this.handleAuthError();
          return Promise.reject(error);
        }
        
        // If error is 401 and we haven't tried to refresh token yet
        if (error.response?.status === 401 && originalRequest && !(originalRequest as any)._retry) {
          // Mark this request as retried to prevent infinite loops (using internal flag to avoid CORS)
          (originalRequest as any)._retry = true;
          
          try {
            // Try to refresh the token
            const refreshToken = localStorage.getItem('refreshToken');
            if (!refreshToken) {
              // No refresh token available, redirect to login
              this.handleAuthError();
              return Promise.reject(error);
            }
            
            // Call refresh token endpoint
            const response = await this.instance.post<ApiSuccessResponse<{
              accessToken: string;
              refreshToken: string;
              expiresIn: number;
            }>>('/auth/refresh', { refreshToken });
            
            if (response.data.success) {
              const { accessToken, refreshToken: newRefreshToken } = response.data.data;
              
              // Store new tokens
              localStorage.setItem('accessToken', accessToken);
              localStorage.setItem('refreshToken', newRefreshToken);
              
              // Update authorization header and retry original request
              originalRequest.headers.Authorization = `Bearer ${accessToken}`;
              return this.instance(originalRequest);
            }
          } catch (refreshError) {
            // Refresh token failed, clear tokens and redirect to login
            this.handleAuthError();
          }
        }
        
        return Promise.reject(error);
      }
    );
  }

  private handleAuthError = (): void => {
    // Clear tokens. Do NOT hard-navigate via window.location.href — that
    // kills in-flight requests (e.g. a fresh /users/me fired immediately
    // after a successful supabase login) and leaves their promises pending
    // forever. The auth slice's fetchCurrentUser.rejected reducer is the
    // single source of truth for clearing Redux state, and ProtectedRoute
    // handles redirecting unauthenticated users.
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');

    // Admin sessions live in localStorage and do not share the Redux auth
    // flow. If the 401 happens while the user is on an admin route, clear the
    // admin session and force them to the admin login page.
    localStorage.removeItem('isAdminAuthenticated');
    localStorage.removeItem('adminUser');
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/mgt-x7k9p2')) {
      window.location.href = '/mgt-x7k9p2/auth';
    }
  }

  // Generic request methods
  get = async <T = any>(url: string, params?: any): Promise<ApiResponse<T>> => {
    try {
      const response: AxiosResponse<ApiResponse<T>> = await this.instance.get(url, { params });
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  post = async <T = any>(url: string, data?: any, options?: { skipAuth?: boolean }): Promise<ApiResponse<T>> => {
    try {
      const response: AxiosResponse<ApiResponse<T>> = await this.instance.post(url, data, {
        ...(options?.skipAuth && { headers: { Authorization: undefined } }),
      });
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  put = async <T = any>(url: string, data?: any): Promise<ApiResponse<T>> => {
    try {
      const response: AxiosResponse<ApiResponse<T>> = await this.instance.put(url, data);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  patch = async <T = any>(url: string, data?: any): Promise<ApiResponse<T>> => {
    try {
      const response: AxiosResponse<ApiResponse<T>> = await this.instance.patch(url, data);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  delete = async <T = any>(url: string): Promise<ApiResponse<T>> => {
    try {
      const response: AxiosResponse<ApiResponse<T>> = await this.instance.delete(url);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Special method for multipart/form-data (file uploads)
  uploadFile = async <T = any>(
    url: string,
    formData: FormData,
    method: 'POST' | 'PATCH' = 'POST',
    options?: UploadOptions,
  ): Promise<ApiResponse<T>> => {
    try {
      const response: AxiosResponse<ApiResponse<T>> = await this.instance.request({
        method,
        url,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: options?.timeout ?? UPLOAD_TIMEOUT_MS,
        onUploadProgress: options?.onUploadProgress,
        signal: options?.signal,
      });
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Special method for SSE streaming with file uploads
  uploadFileWithSSE = async (
    url: string,
    formData: FormData,
    onProgress: (event: any) => void
  ): Promise<void> => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      throw new Error('Authentication required');
    }

    const fullUrl = `${BASE_URL}${url}`;
    
    const response = await fetch(fullUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'text/event-stream',
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('Stream reader not available');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      
      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const event = JSON.parse(line.slice(6));
            onProgress(event);
          } catch (e) {
            console.error('Failed to parse SSE event:', e);
          }
        }
      }
    }
  }

  getBaseUrl = (): string => {
    return BASE_URL;
  }

  private handleError = (error: any): ApiErrorResponse => {
    if (axios.isAxiosError(error) && error.response) {
      const body = error.response.data as ApiErrorResponse;
      // Surface a single toast for buyer-account write rejections so every
      // creator-only call site doesn't have to handle this case manually.
      // The response is still returned untouched so callers can react.
      if (body?.error?.code === 'BUYER_FORBIDDEN') {
        // Lazy-import to avoid circular toast dependencies during module init.
        import('react-hot-toast').then(({ toast }) => {
          toast.error(
            body.error.message ||
              'This action isn\'t available for buyer accounts.',
          );
        });
      }
      return body;
    }

    // No response came back: the request died in transit. Preserve *why* so
    // the UI can tell a slow connection apart from a server fault instead of
    // reporting every network blip as a 500.
    return {
      success: false,
      error: {
        code: this.transportCode(error),
        message: 'An unexpected error occurred',
        details: error?.message,
      },
    };
  }

  /** Classify a response-less axios failure into one of our transport codes. */
  private transportCode = (error: any): string => {
    if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') {
      return ERR.REQUEST_CANCELLED;
    }
    if (error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT') {
      return ERR.NETWORK_TIMEOUT;
    }
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return ERR.NETWORK_OFFLINE;
    }
    // ERR_NETWORK and friends: reachable device, unreachable server.
    return ERR.NETWORK_OFFLINE;
  }
}

// Export a singleton instance
export const apiClient = new ApiClient();
